'use client';
import { createContext, useContext } from 'react';

export const DEFAULT_COMP_CONFIG = {
  colorPrimary: '#2563eb',
  colorSecondary: '#1e40af',
  colorBg: '#f6f7f9',
  colorText: '#2e2e2e',
  colorForeground: '#ffffff',
  showTitle: true,
};

const CONFIG_CSS_VARS = {
  colorPrimary: ['--theme-primary'],
  colorSecondary: ['--theme-highlight'],
  colorBg: ['--theme-bg'],
  colorBgMuted: ['--theme-bg-muted'],
  colorText: ['--theme-text'],
  colorForeground: ['--theme-foreground'],
  colorOnPrimary: ['--theme-on-primary', '--theme-on-cta'],
  colorBorder: ['--theme-border'],
  colorBtnBorder: ['--theme-btn-border'],
  colorTextMuted: ['--theme-text-muted', '--theme-subtext', '--text-half-score'],
  colorAccent: ['--theme-accent'],
  colorPageBg: ['--kbv-bg-page'],
  colorCardBg: ['--kbv-card-bg'],
  colorCardBorder: ['--kbv-card-border'],
  colorPrimaryHover: ['--theme-primary-hover'],
  colorGlow: ['--theme-glow'],
  colorTableHeaderStart: ['--theme-table-header-bg-start'],
  colorTableHeaderEnd: ['--theme-table-header-bg-end'],
  colorTrendUp: ['--theme-trend-up'],
  colorTrendDown: ['--theme-trend-down'],
  colorOddsValue: ['--theme-odds-value'],
  colorCtaGradient: ['--theme-cta-gradient'],
  colorLiveShadow: ['--theme-live-shadow'],
};

const normalizeDataConfigRaw = (raw) =>
  String(raw)
    .trim()
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, '&');

export const parseCompConfigObject = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(normalizeDataConfigRaw(raw));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const applyCompConfigCssVars = (element, parsed) => {
  if (!element || !parsed || typeof parsed !== 'object') return;

  for (const [key, vars] of Object.entries(CONFIG_CSS_VARS)) {
    const value = parsed[key];
    if (value == null || value === '') continue;
    vars.forEach((name) => element.style.setProperty(name, String(value)));
  }

  if (parsed.colorPageBg == null && parsed.colorBg != null) {
    element.style.setProperty('--kbv-bg-page', String(parsed.colorBg));
  }
  if (parsed.colorCardBg == null && parsed.colorForeground != null) {
    element.style.setProperty('--kbv-card-bg', String(parsed.colorForeground));
  }
  if (parsed.colorCardBorder == null && parsed.colorBorder != null) {
    element.style.setProperty('--kbv-card-border', String(parsed.colorBorder));
  }
  if (parsed.colorPrimaryHover == null && parsed.colorSecondary != null) {
    element.style.setProperty('--theme-primary-hover', String(parsed.colorSecondary));
  }
  if (parsed.colorGlow == null && parsed.colorPrimary != null) {
    element.style.setProperty(
      '--theme-glow',
      `color-mix(in srgb, ${parsed.colorPrimary} 25%, transparent)`,
    );
  }
  if (
    parsed.colorCtaGradient == null &&
    parsed.colorSecondary != null &&
    parsed.colorPrimary != null
  ) {
    element.style.setProperty(
      '--theme-cta-gradient',
      `linear-gradient(180deg, ${parsed.colorSecondary}, ${parsed.colorPrimary})`,
    );
  }
  if (parsed.colorLiveShadow == null && parsed.colorSecondary != null) {
    element.style.setProperty(
      '--theme-live-shadow',
      `0 0 8px color-mix(in srgb, ${parsed.colorSecondary} 25%, transparent)`,
    );
  }
};

const CompConfigContext = createContext({
  ...DEFAULT_COMP_CONFIG,
  hasDataConfig: false,
  parsedConfig: null,
});

export const CompConfigProvider = ({
  config,
  hasDataConfig = false,
  parsedConfig = null,
  children,
}) => {
  const merged = {
    ...DEFAULT_COMP_CONFIG,
    ...config,
    hasDataConfig,
    parsedConfig,
  };
  return (
    <CompConfigContext.Provider value={merged}>
      {children}
    </CompConfigContext.Provider>
  );
};

export const useCompConfig = () => useContext(CompConfigContext);
